import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StammkundeService } from '../../../Services/stammkunde.service';
import { CaptureQueueService, QueuedCapture } from '../../../Services/capture-queue.service';
import { AuthService } from '../../../api-authorization/authorize.service';
import { AuftragAssignment, CustomerRef } from '../../../Models/stammkunde.model';
import { ZeitPerson } from '../zeit-eintrag-form/zeit-eintrag-form.component';
import { StundenErfassungComponent } from '../stunden-erfassung/stunden-erfassung.component';
import { MaterialErfassungComponent } from '../material-erfassung/material-erfassung.component';
import { SpontanEinsatzFormComponent } from '../spontan-einsatz-form/spontan-einsatz-form.component';
import { TakedaTagesberichtComponent, TakedaTagesberichtData } from '../takeda-tagesbericht/takeda-tagesbericht.component';

interface DayGroup {
  day: Date;
  items: AuftragAssignment[];
}

type RangeFilter = 'heute' | 'woche' | 'alle';

/**
 * Mobile-freundliche Feld-Sicht für den Vorarbeiter: eigene Einsätze, schnelle
 * Statuswechsel (Bestätigt/Unterwegs/Erledigt) sowie Stunden- und Material-Erfassung.
 * Alle Erfassungen sind offline-fähig und werden bei Verbindung nachgesendet.
 */
@Component({
  selector: 'app-meine-einsaetze',
  templateUrl: './meine-einsaetze.component.html',
  styleUrls: ['./meine-einsaetze.component.css', '../stammkunden-theme.css']
})
export class MeineEinsaetzeComponent implements OnInit, OnDestroy {
  loading = true;
  today = new Date();
  allAssignments: AuftragAssignment[] = [];
  dayGroups: DayGroup[] = [];
  range: RangeFilter = 'woche';
  showDone = false;
  customers: CustomerRef[] = [];

  online = true;
  pending: QueuedCapture[] = [];
  syncing = false;

  /** Von mir gebuchte Stunden je Einsatz (assignmentId -> Summe). */
  bookedHours: Record<number, number> = {};

  private subs = new Subscription();

  constructor(
    private stammkundeService: StammkundeService,
    private captureQueue: CaptureQueueService,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  /** Zum Auftrag springen darf nur, wer die Stammkunden-Verwaltung sehen darf. */
  get canOpenAuftrag(): boolean {
    return this.authService.canAccessStammkunden();
  }

  ngOnInit(): void {
    this.subs.add(this.captureQueue.online$.subscribe(v => {
      const cameOnline = !this.online && v;
      this.online = v;
      if (cameOnline) { this.syncNow(); }
    }));
    this.subs.add(this.captureQueue.pending$.subscribe(p => this.pending = p));
    this.load();
    // Stammkunden für den Spontan-Einsatz laden (fehlertolerant – Button bleibt sonst leer).
    this.stammkundeService.getMyCustomers().subscribe({
      next: list => this.customers = list ?? [],
      error: () => { /* Spontan-Anlegen dann nicht möglich */ }
    });
    // Beim Öffnen versuchen, offline erfasste Vorgänge nachzusenden.
    if (this.captureQueue.pendingCount > 0) { this.syncNow(); }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get pendingCount(): number {
    return this.pending.length;
  }

  load(): void {
    this.loading = true;
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
    const to = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 90);
    this.stammkundeService.getMyAssignments(this.toIso(from), this.toIso(to)).subscribe({
      next: list => {
        this.allAssignments = list ?? [];
        this.rebuild();
        this.loading = false;
        this.loadBookedHours();
      },
      error: () => {
        this.snackBar.open('Einsätze konnten nicht geladen werden.', 'OK', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  /** Von mir gebuchte Stunden je Einsatz laden (für die Anzeige auf der Karte). */
  private loadBookedHours(): void {
    const auftragIds = Array.from(new Set(
      this.allAssignments.map(a => a.auftragId).filter((id): id is number => id != null)));
    if (auftragIds.length === 0) { return; }

    forkJoin(auftragIds.map(id =>
      this.stammkundeService.getTimeEntries(id).pipe(catchError(() => of([])))
    )).subscribe(results => {
      const map: Record<number, number> = {};
      for (const entries of results) {
        for (const e of entries) {
          if (e.assignmentId != null) {
            map[e.assignmentId] = (map[e.assignmentId] || 0) + e.hours;
          }
        }
      }
      this.bookedHours = map;
    });
  }

  bookedHoursFor(e: AuftragAssignment): number {
    return this.bookedHours[e.id] || 0;
  }

  setRange(range: RangeFilter): void {
    this.range = range;
    this.rebuild();
  }

  toggleShowDone(): void {
    this.showDone = !this.showDone;
    this.rebuild();
  }

  /** Anzahl erledigter Einsätze im aktuellen Zeitraum (für den Umschalter). */
  get doneCount(): number {
    return this.allAssignments.filter(e => e.workStatus === 'Done' && this.inRange(new Date(e.start))).length;
  }

  private rebuild(): void {
    const filtered = this.allAssignments.filter(e =>
      this.inRange(new Date(e.start)) && (this.showDone || e.workStatus !== 'Done'));
    const groups = new Map<string, DayGroup>();
    const sorted = [...filtered].sort((a, b) => a.start.localeCompare(b.start));
    for (const e of sorted) {
      const d = new Date(e.start);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!groups.has(key)) {
        groups.set(key, { day: new Date(d.getFullYear(), d.getMonth(), d.getDate()), items: [] });
      }
      groups.get(key)!.items.push(e);
    }
    this.dayGroups = Array.from(groups.values());
  }

  private inRange(start: Date): boolean {
    const now = new Date();
    if (this.range === 'alle') { return true; }
    if (this.range === 'heute') {
      return start.getFullYear() === now.getFullYear()
        && start.getMonth() === now.getMonth()
        && start.getDate() === now.getDate();
    }
    // 'woche': Montag–Sonntag der aktuellen Woche
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dow = (monday.getDay() + 6) % 7; // Mo=0 … So=6
    monday.setDate(monday.getDate() - dow);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 7);
    return start >= monday && start < sunday;
  }

  // ── Schnelle Statuswechsel ──

  workStatusLabel(status: string): string {
    switch (status) {
      case 'Planned': return 'Geplant';
      case 'Confirmed': return 'Bestätigt';
      case 'EnRoute': return 'Unterwegs';
      case 'Done': return 'Erledigt';
      default: return status;
    }
  }

  async setStatus(e: AuftragAssignment, status: string): Promise<void> {
    if (!e.auftragId || e.workStatus === status) { return; }
    const previous = e.workStatus;
    e.workStatus = status; // optimistisch
    this.rebuild(); // erledigte Einsätze ggf. ausblenden
    try {
      const label = `Status ${this.workStatusLabel(status)} · ${e.auftragTitle || e.title}`;
      const res = await this.captureQueue.captureWorkStatus(e.auftragId, e.id, status, label);
      if (res.queued) {
        this.snackBar.open('Offline gespeichert – wird nachgesendet.', 'OK', { duration: 3000 });
      }
    } catch (err: any) {
      e.workStatus = previous;
      this.rebuild();
      const message = err?.error?.message || 'Status konnte nicht gesetzt werden.';
      this.snackBar.open(message, 'OK', { duration: 4000 });
    }
  }

  /** Ein noch nicht angenommener Einsatz ist ein offenes "Ticket". */
  isOpenTicket(e: AuftragAssignment): boolean {
    return e.workStatus === 'Planned';
  }

  get openTicketCount(): number {
    return this.allAssignments.filter(e => this.isOpenTicket(e)).length;
  }

  /** Ticket annehmen: Einsatz auf "Bestätigt" setzen. */
  accept(e: AuftragAssignment): Promise<void> {
    return this.setStatus(e, 'Confirmed');
  }

  // ── Spontan-Einsatz anlegen ──

  openSpontan(): void {
    if (!this.online) {
      this.snackBar.open('Spontan-Einsätze brauchen eine Verbindung.', 'OK', { duration: 4000 });
      return;
    }
    const ref = this.dialog.open(SpontanEinsatzFormComponent, {
      width: '620px',
      maxWidth: '96vw',
      data: { customers: this.customers }
    });
    ref.afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open('Spontan-Einsatz angelegt.', 'OK', { duration: 3000 });
        this.load();
      }
    });
  }

  // ── Stunden erfassen ──

  erfasseStunden(e: AuftragAssignment): void {
    if (!e.auftragId) { return; }
    const auftragId = e.auftragId;
    const customerId = e.customerId ?? 0;
    const planned = this.buildPeople(e);

    // Vollständigen Roster (Team + Arbeiter des Kunden) laden – bei Offline nur die Kolonne.
    const openWith = (roster: ZeitPerson[]) => {
      const ref = this.dialog.open(StundenErfassungComponent, {
        width: '560px',
        maxWidth: '96vw',
        data: {
          auftrag: { id: auftragId, title: e.auftragTitle || e.title },
          assignmentId: e.id,
          customerId,
          einsatzLabel: `${this.formatTime(e.start)} · ${e.auftragTitle || e.title}`,
          defaultDate: this.toDateInput(new Date(e.start)),
          plannedPeople: planned,
          rosterPeople: roster.length ? roster : planned,
          useQueue: true
        }
      });
      ref.afterClosed().subscribe(result => {
        if (result?.ok) {
          const parts: string[] = [];
          if (result.created) { parts.push(`${result.created} erfasst`); }
          if (result.updated) { parts.push(`${result.updated} geändert`); }
          if (result.queued) { parts.push(`${result.queued} offline`); }
          this.snackBar.open(parts.length ? `Stunden: ${parts.join(', ')}.` : 'Keine Änderungen.',
            'OK', { duration: 3000 });
          if (parts.length) { this.loadBookedHours(); }
        }
      });
    };

    if (!this.online || !customerId) {
      openWith(planned);
      return;
    }

    forkJoin({
      members: this.stammkundeService.getMembers(customerId).pipe(catchError(() => of([]))),
      workers: this.stammkundeService.getWorkers(customerId).pipe(catchError(() => of([])))
    }).subscribe(res => {
      const roster: ZeitPerson[] = [
        ...(res.members ?? []).map(m => ({ label: m.userName || m.email || m.userId, personUserId: m.userId })),
        ...(res.workers ?? []).map(w => ({ label: w.name, workerId: w.id }))
      ];
      openWith(roster);
    });
  }

  // ── Material erfassen ──

  erfasseMaterial(e: AuftragAssignment): void {
    if (!e.auftragId) { return; }
    const label = `${this.formatTime(e.start)} · ${e.auftragTitle || e.title}`;
    const ref = this.dialog.open(MaterialErfassungComponent, {
      width: '620px',
      maxWidth: '96vw',
      data: {
        auftrag: { id: e.auftragId, title: e.auftragTitle || e.title } as any,
        assignmentId: e.id,
        einsatzLabel: label,
        materials: e.materials ?? [],
        useQueue: true
      }
    });
    ref.afterClosed().subscribe(result => {
      if (result?.ok) {
        e.materials = result.materials; // optimistisch übernehmen
        this.snackBar.open(
          result.queued ? 'Material offline gespeichert – wird nachgesendet.' : 'Material erfasst.',
          'OK', { duration: 3000 });
      }
    });
  }

  // ── Takeda-Tagesbericht (ersetzt bei Takeda die separate Stunden-/Material-Erfassung) ──

  openTagesbericht(e: AuftragAssignment): void {
    if (!e.auftragId) {
      this.snackBar.open('Dieser Einsatz ist keinem Auftrag zugeordnet.', 'OK', { duration: 4000 });
      return;
    }
    if (!this.online) {
      this.snackBar.open('Der Tagesbericht ist nur online verfügbar. Bitte bei Verbindung erneut öffnen.',
        'OK', { duration: 5000 });
      return;
    }
    const customerId = e.customerId ?? 0;
    const planned = this.buildPeople(e);

    const openWith = (roster: ZeitPerson[]) => {
      const ref = this.dialog.open(TakedaTagesberichtComponent, {
        width: '800px',
        maxWidth: '96vw',
        maxHeight: '94vh',
        autoFocus: false,
        disableClose: true, // Schließen übernimmt der Dialog (Warnung bei ungespeicherten Änderungen)
        data: {
          assignment: e,
          workDate: this.toDateInput(new Date(e.start)),
          plannedPeople: planned,
          rosterPeople: roster.length ? roster : planned
        } as TakedaTagesberichtData
      });
      ref.afterClosed().subscribe(result => {
        // Abschließen legt serverseitig normale Stunden-Einträge an -> Anzeige aktualisieren.
        if (result?.submitted) { this.loadBookedHours(); }
      });
    };

    if (!customerId) {
      openWith(planned);
      return;
    }

    // Auswahl für weitere Personen: Team-Mitglieder + Arbeiter des Stammkunden.
    forkJoin({
      members: this.stammkundeService.getMembers(customerId).pipe(catchError(() => of([]))),
      workers: this.stammkundeService.getWorkers(customerId).pipe(catchError(() => of([])))
    }).subscribe(res => {
      openWith([
        ...(res.members ?? []).map(m => ({ label: m.userName || m.email || m.userId, personUserId: m.userId })),
        ...(res.workers ?? []).map(w => ({ label: w.name, workerId: w.id }))
      ]);
    });
  }

  // ── Synchronisierung ──

  async syncNow(): Promise<void> {
    if (this.syncing || this.captureQueue.pendingCount === 0) { return; }
    this.syncing = true;
    try {
      const summary = await this.captureQueue.flush();
      if (summary.sent > 0) {
        this.snackBar.open(`${summary.sent} Erfassung(en) nachgesendet.`, 'OK', { duration: 3000 });
        this.load();
      }
      if (summary.dropped > 0) {
        this.snackBar.open(
          `${summary.dropped} Erfassung(en) verworfen (Einsatz nicht mehr verfügbar).`, 'OK', { duration: 5000 });
      }
    } finally {
      this.syncing = false;
    }
  }

  // ── Helpers ──

  buildPeople(e: AuftragAssignment): ZeitPerson[] {
    const people: ZeitPerson[] = [];
    if (e.assignedUserId) {
      people.push({ label: e.assignedUserName || e.assignedUserId, personUserId: e.assignedUserId });
    }
    (e.participantUserIds ?? []).forEach((uid, i) => {
      people.push({ label: (e.participantNames ?? [])[i] || uid, personUserId: uid });
    });
    (e.workerIds ?? []).forEach((wid, i) => {
      people.push({ label: (e.workerNames ?? [])[i] || `Arbeiter #${wid}`, workerId: wid });
    });
    return people;
  }

  openAuftrag(e: AuftragAssignment): void {
    if (this.canOpenAuftrag && e.customerId && e.auftragId) {
      this.router.navigate(['/stammkunden', e.customerId, 'auftrag', e.auftragId]);
    }
  }

  materialSummary(e: AuftragAssignment): string {
    const list = e.materials ?? [];
    const shown = list.slice(0, 2).map(m => `${m.quantity} ${m.unit} ${m.name}`);
    const more = list.length - shown.length;
    return shown.join(', ') + (more > 0 ? ` +${more}` : '');
  }

  formatTime(iso: string): string {
    const d = new Date(iso);
    return `${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}`;
  }

  trackByDay(_: number, g: DayGroup): number {
    return g.day.getTime();
  }

  trackByEinsatz(_: number, e: AuftragAssignment): number {
    return e.id;
  }

  private toIso(date: Date): string {
    return `${this.toDateInput(date)}T00:00:00`;
  }

  private toDateInput(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
